
import React, { useState } from "react";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Target, TrendingUp, Eye, Dices, Save, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SharePuxadasDialog from "@/components/SharePuxadasDialog";
import PageInstructions from "@/components/PageInstructions";

const PuxadasPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getTrialInfo } = useAuth();
  const [selectedAnimal, setSelectedAnimal] = useState<string>("");
  const [gameCount, setGameCount] = useState<string>("5");

  const trialInfo = getTrialInfo();
  const isAccessExpired = trialInfo.status === 'trial_expired' || trialInfo.status === 'subscription_expired';

  // Dados atualizados dos 25 bichos do jogo do bicho com dezenas corretas
  const animais = [
    { id: "01", nome: "Avestruz", dezenas: ["01", "02", "03", "04"], puxadas: ["Vaca (25)", "Águia (02)", "Galo (13)", "Pavão (19)", "Peru (20)"], emoji: "🦤" },
    { id: "02", nome: "Águia", dezenas: ["05", "06", "07", "08"], puxadas: ["Coelho (10)", "Avestruz (01)", "Galo (13)", "Pavão (19)", "Peru (20)"], emoji: "🦅" },
    { id: "03", nome: "Burro", dezenas: ["09", "10", "11", "12"], puxadas: ["Cavalo (11)", "Elefante (12)", "Touro (21)", "Veado (24)", "Coelho (10)", "Cobra (09)"], emoji: "🫏" },
    { id: "04", nome: "Borboleta", dezenas: ["13", "14", "15", "16"], puxadas: ["Cabra (06)", "Elefante (12)", "Gato (14)", "Leão (16)", "Cachorro (05)", "Galo (13)"], emoji: "🦋" },
    { id: "05", nome: "Cachorro", dezenas: ["17", "18", "19", "20"], puxadas: ["Galo (13)", "Gato (14)", "Camelo (08)", "Macaco (17)", "Porco (18)", "Pavão (19)"], emoji: "🐕" },
    { id: "06", nome: "Cabra", dezenas: ["21", "22", "23", "24"], puxadas: ["Carneiro (07)", "Macaco (17)", "Elefante (12)", "Touro (21)", "Tigre (22)", "Urso (23)"], emoji: "🐐" },
    { id: "07", nome: "Carneiro", dezenas: ["25", "26", "27", "28"], puxadas: ["Cabra (06)", "Coelho (10)", "Vaca (25)"], emoji: "🐏" },
    { id: "08", nome: "Camelo", dezenas: ["29", "30", "31", "32"], puxadas: ["Cachorro (05)", "Elefante (12)", "Urso (23)"], emoji: "🐪" },
    { id: "09", nome: "Cobra", dezenas: ["33", "34", "35", "36"], puxadas: ["Jacaré (15)", "Porco (18)", "Burro (03)", "Gato (14)"], emoji: "🐍" },
    { id: "10", nome: "Coelho", dezenas: ["37", "38", "39", "40"], puxadas: ["Carneiro (07)", "Águia (02)", "Burro (03)"], emoji: "🐰" },
    { id: "11", nome: "Cavalo", dezenas: ["41", "42", "43", "44"], puxadas: ["Burro (03)", "Cabra (06)", "Touro (21)"], emoji: "🐎" },
    { id: "12", nome: "Elefante", dezenas: ["45", "46", "47", "48"], puxadas: ["Cabra (06)", "Urso (23)", "Tigre (22)", "Leão (16)", "Burro (03)"], emoji: "🐘" },
    { id: "13", nome: "Galo", dezenas: ["49", "50", "51", "52"], puxadas: ["Cachorro (05)", "Avestruz (01)", "Águia (02)", "Pavão (19)", "Peru (20)"], emoji: "🐓" },
    { id: "14", nome: "Gato", dezenas: ["53", "54", "55", "56"], puxadas: ["Cachorro (05)", "Leão (16)", "Tigre (22)", "Cobra (09)"], emoji: "🐱" },
    { id: "15", nome: "Jacaré", dezenas: ["57", "58", "59", "60"], puxadas: ["Cobra (09)", "Porco (18)", "Borboleta (04)", "Macaco (17)"], emoji: "🐊" },
    { id: "16", nome: "Leão", dezenas: ["61", "62", "63", "64"], puxadas: ["Elefante (12)", "Gato (14)", "Tigre (22)", "Urso (23)"], emoji: "🦁" },
    { id: "17", nome: "Macaco", dezenas: ["65", "66", "67", "68"], puxadas: ["Cachorro (05)", "Cabra (06)", "Peru (20)", "Jacaré (15)"], emoji: "🐒" },
    { id: "18", nome: "Porco", dezenas: ["69", "70", "71", "72"], puxadas: ["Cobra (09)", "Peru (20)", "Jacaré (15)", "Cachorro (05)"], emoji: "🐷" },
    { id: "19", nome: "Pavão", dezenas: ["73", "74", "75", "76"], puxadas: ["Avestruz (01)", "Águia (02)", "Galo (13)", "Peru (20)"], emoji: "🦚" },
    { id: "20", nome: "Peru", dezenas: ["77", "78", "79", "80"], puxadas: ["Avestruz (01)", "Águia (02)", "Galo (13)", "Pavão (19)", "Veado (24)"], emoji: "🦃" },
    { id: "21", nome: "Touro", dezenas: ["81", "82", "83", "84"], puxadas: ["Vaca (25)", "Burro (03)", "Cabra (06)"], emoji: "🐂" },
    { id: "22", nome: "Tigre", dezenas: ["85", "86", "87", "88"], puxadas: ["Gato (14)", "Leão (16)", "Cabra (06)"], emoji: "🐅" },
    { id: "23", nome: "Urso", dezenas: ["89", "90", "91", "92"], puxadas: ["Leão (16)", "Elefante (12)", "Camelo (08)", "Cabra (06)"], emoji: "🐻" },
    { id: "24", nome: "Veado", dezenas: ["93", "94", "95", "96"], puxadas: ["Peru (20)", "Burro (03)", "Cabra (06)"], emoji: "🦌" },
    { id: "25", nome: "Vaca", dezenas: ["97", "98", "99", "00"], puxadas: ["Touro (21)", "Avestruz (01)", "Carneiro (07)"], emoji: "🐄" }
  ];

  const selectedAnimalData = animais.find(animal => animal.id === selectedAnimal);
  const selectedCount = parseInt(gameCount);

  // Função melhorada para gerar centenas usando todas as dezenas
  const generateCentenas = (dezenas: string[], count: number) => {
    const centenas: string[] = [];
    
    // Usar todas as dezenas para gerar centenas
    dezenas.forEach(dezena => {
      for (let i = 1; i <= 9; i++) {
        centenas.push(`${i}${dezena}`);
      }
    });
    
    // Embaralhar e retornar a quantidade solicitada
    const shuffled = centenas.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  // Função melhorada para gerar milhares usando todas as dezenas
  const generateMilhares = (dezenas: string[], count: number) => {
    const milhares: string[] = [];
    
    // Usar todas as dezenas para gerar milhares
    dezenas.forEach(dezena => {
      for (let i = 10; i <= 99; i++) {
        milhares.push(`${i}${dezena}`);
      }
    });
    
    // Embaralhar e retornar a quantidade solicitada
    const shuffled = milhares.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  const handleSaveJogos = async () => {
    if (!selectedAnimalData || !user) {
      toast.error("Faça login para salvar seus jogos");
      return;
    }

    // Verificar se o usuário tem acesso premium válido
    const hasActivePlan = user.plan && user.plan !== 'free' && !isAccessExpired;
    
    if (!hasActivePlan) {
      toast.error("🔒 Recurso exclusivo para usuários com plano ativo! Faça upgrade para salvar seus jogos.", {
        duration: 5000,
        action: {
          label: "Ver Planos",
          onClick: () => navigate("/planos#escolha-seu-plano")
        }
      });
      return;
    }

    const centenas = generateCentenas(selectedAnimalData.dezenas, selectedCount);
    const milhares = generateMilhares(selectedAnimalData.dezenas, selectedCount);

    try {
      const { error } = await supabase
        .from("puxadas_jogos")
        .insert({
          user_id: user.id,
          animal_id: selectedAnimalData.id,
          animal_nome: selectedAnimalData.nome,
          quantidade_jogos: selectedCount,
          centenas: centenas,
          milhares: milhares
        });

      if (error) throw error;

      toast.success("💾 Jogos salvos com sucesso! Válidos por 5 dias na sua conta PREMIUM.", {
        duration: 4000
      });
    } catch (error) {
      console.error("Erro ao salvar jogos:", error);
      toast.error("Erro ao salvar jogos");
    }
  };

  const handleGeneratePalpite = () => {
    if (selectedAnimalData) {
      navigate(`/gerar-jogos?animal=${selectedAnimalData.id}&nome=${selectedAnimalData.nome}`);
    }
  };

  return (
    <>
      <SEOHead
        title="Puxadas Jogo do Bicho - Sequências, Centenas e Milhares por Animal"
        description="Descubra quais bichos têm maior probabilidade de sair juntos. Puxadas com centenas e milhares derivadas de cada grupo. Análise estatística das dezenas sorteadas no Jogo do Bicho."
        keywords="puxadas jogo do bicho, puxadas bicho, sequências bicho, centenas por animal, milhares por grupo, probabilidade bicho, bichos que saem juntos"
        canonical="/puxadas"
      />
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-8">
        {/* Header melhorado */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            🔮 Puxadas do Jogo do Bicho
          </h1>
          <p className="text-2xl font-bold text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            🎯 <span className="text-primary font-extrabold">DESCUBRA</span> quais bichos têm maior probabilidade de sair juntos! 
            <br />
            📊 Baseado em <span className="text-green-600 font-extrabold">ANÁLISES ESTATÍSTICAS</span> das dezenas sorteadas. 
            <br />
            💰 Explore <span className="text-yellow-600 font-extrabold">CENTENAS e MILHARES</span> derivadas de cada grupo para maximizar seus ganhos!
          </p>
        </div>

        <PageInstructions
          title="📖 Como usar as Puxadas"
          steps={[
            "Escolha um dos 25 bichos na lista abaixo",
            "Selecione quantos jogos (centenas e milhares) deseja gerar",
            "O sistema mostra quais bichos costumam sair junto com o escolhido",
            "Veja as centenas e milhares derivadas de cada grupo",
            "Salve ou compartilhe seus palpites com o botão no final"
          ]}
        />

        {/* Seletor de Bicho com fonte maior */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Target className="h-6 w-6" />
              Selecione um Bicho
            </CardTitle>
            <CardDescription className="text-lg">
              Escolha um dos 25 bichos para ver suas puxadas e combinações
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedAnimal} onValueChange={setSelectedAnimal}>
              <SelectTrigger className="w-full text-xl font-bold h-14">
                <SelectValue placeholder="Escolha um bicho..." />
              </SelectTrigger>
              <SelectContent>
                {animais.map((animal) => (
                  <SelectItem key={animal.id} value={animal.id} className="text-xl font-bold">
                    {animal.emoji} {animal.nome} (Grupo {animal.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Seletor de Quantidade de Jogos com fonte maior */}
        {selectedAnimal && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Dices className="h-6 w-6" />
                Quantidade de Jogos
              </CardTitle>
              <CardDescription className="text-lg">
                Escolha quantos palpites de milhar e centena deseja gerar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={gameCount} onValueChange={setGameCount}>
                <SelectTrigger className="w-full text-xl font-bold h-14">
                  <SelectValue placeholder="Selecione a quantidade..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4" className="text-xl font-bold">4 jogos</SelectItem>
                  <SelectItem value="5" className="text-xl font-bold">5 jogos (Padrão)</SelectItem>
                  <SelectItem value="6" className="text-xl font-bold">6 jogos</SelectItem>
                  <SelectItem value="7" className="text-xl font-bold">7 jogos</SelectItem>
                  <SelectItem value="8" className="text-xl font-bold">8 jogos</SelectItem>
                  <SelectItem value="9" className="text-xl font-bold">9 jogos</SelectItem>
                  <SelectItem value="10" className="text-xl font-bold">10 jogos</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Informações do Bicho Selecionado */}
        {selectedAnimalData && (
          <div className="space-y-6">
            {/* Puxadas movidas para cima */}
            <Card className="border-green-500/20 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300 text-2xl">
                  <TrendingUp className="h-6 w-6" />
                  🔁 Puxadas do {selectedAnimalData.nome}
                </CardTitle>
                <CardDescription className="text-green-600 dark:text-green-400 text-lg font-semibold">
                  Estes bichos costumam sair logo após o {selectedAnimalData.nome}!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {selectedAnimalData.puxadas.map((puxada, index) => (
                    <Badge 
                      key={index} 
                      className="bg-green-500 hover:bg-green-600 text-white text-lg px-4 py-3 font-bold"
                    >
                      ➡️ {puxada}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Card Principal do Bicho */}
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardHeader className="text-center">
                <CardTitle className="text-4xl">
                  {selectedAnimalData.emoji} {selectedAnimalData.nome}
                </CardTitle>
                <CardDescription className="text-xl">
                  Grupo {selectedAnimalData.id}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Dezenas */}
                <div>
                  <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                    🎯 Dezenas do {selectedAnimalData.nome}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedAnimalData.dezenas.map((dezena) => (
                      <Badge key={dezena} variant="outline" className="text-2xl px-4 py-2 font-bold">
                        {dezena}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Centenas com fonte 32px */}
                <div>
                  <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                    🔢 Centenas Geradas ({selectedCount} jogos)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {generateCentenas(selectedAnimalData.dezenas, selectedCount).map((centena, index) => (
                      <Badge key={`${centena}-${index}`} variant="secondary" className="text-center text-[32px] py-4 px-6 font-extrabold">
                        {centena}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Milhares com fonte 32px */}
                <div>
                  <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                    💥 Milhares Gerados ({selectedCount} jogos)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {generateMilhares(selectedAnimalData.dezenas, selectedCount).map((milhar, index) => (
                      <Badge key={`${milhar}-${index}`} variant="outline" className="text-center text-[32px] py-4 px-6 font-extrabold">
                        {milhar}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="flex flex-wrap gap-4 justify-center pt-4">
                  {/* Botão Compartilhar */}
                  <SharePuxadasDialog
                    animalNome={selectedAnimalData.nome}
                    centenas={generateCentenas(selectedAnimalData.dezenas, selectedCount)}
                    milhares={generateMilhares(selectedAnimalData.dezenas, selectedCount)}
                    puxadas={selectedAnimalData.puxadas}
                  />

                  {/* Botão para salvar jogos - Apenas para Premium */}
                   {user && (
                    <Button 
                      onClick={handleSaveJogos}
                      className={`px-6 py-3 text-lg ${
                        user.plan !== 'free' && !isAccessExpired
                          ? "bg-blue-500 hover:bg-blue-600 text-white"
                          : "bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-600 hover:to-yellow-600 text-black"
                      }`}
                    >
                      {user.plan !== 'free' && !isAccessExpired ? (
                        <>
                          <Save className="mr-2 h-5 w-5" />
                          💾 Salvar Jogos por 5 Dias
                        </>
                      ) : (
                        <>
                          <Crown className="mr-2 h-5 w-5" />
                          🔒 Salvar Jogos (Premium)
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Aviso Premium */}
                {(!user || user.plan === 'free' || isAccessExpired) && (
                  <div className="bg-gradient-to-r from-gold-50 to-yellow-50 dark:from-gold-900/20 dark:to-yellow-900/20 border-2 border-gold-400 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-3">
                      <Crown className="w-8 h-8 text-gold-500" />
                      <div>
                        <h4 className="font-bold text-gold-700 dark:text-gold-300">
                          🏆 Recurso PREMIUM
                        </h4>
                        <p className="text-gold-600 dark:text-gold-400">
                          Salvar dados da puxada somente para usuário PREMIUM. Upgrade para acessar!
                        </p>
                        <Button 
                          onClick={() => navigate("/planos#escolha-seu-plano")}
                          className="mt-2 bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-600 hover:to-yellow-600 text-black"
                          size="sm"
                        >
                          Ver Planos Premium
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Botão de Ação */}
            <Card className="border-yellow-500/20 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950/20 dark:to-yellow-900/20">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-semibold text-yellow-700 dark:text-yellow-300">
                    Gostou desta puxada?
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Use nossa IA avançada para gerar palpites baseados nesta combinação
                  </p>
                  <Button 
                    onClick={handleGeneratePalpite}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-4 text-xl"
                  >
                    <Sparkles className="mr-2 h-6 w-6" />
                    🎯 Gerar Palpite com base nessa Puxada
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ranking dos Bichos Mais Puxados */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Eye className="h-6 w-6" />
                  📊 Bichos Mais Puxados da Semana
                </CardTitle>
                <CardDescription className="text-lg">
                  Ranking baseado em análises estatísticas dos últimos resultados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { emoji: "🦁", nome: "Leão", porcentagem: "23%" },
                    { emoji: "🐍", nome: "Cobra", porcentagem: "19%" },
                    { emoji: "🐎", nome: "Cavalo", porcentagem: "17%" },
                    { emoji: "🐓", nome: "Galo", porcentagem: "15%" },
                    { emoji: "🦚", nome: "Pavão", porcentagem: "12%" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold">
                          {index + 1}
                        </Badge>
                        <span className="text-2xl">{item.emoji}</span>
                        <span className="font-medium text-xl">{item.nome}</span>
                      </div>
                      <Badge variant="secondary" className="text-lg px-3 py-1">{item.porcentagem}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Call to Action quando nenhum bicho está selecionado */}
        {!selectedAnimal && (
          <Card className="text-center py-16">
            <CardContent>
              <div className="space-y-6">
                <div className="text-8xl">🎲</div>
                <h3 className="text-3xl font-semibold">Escolha um Bicho</h3>
                <p className="text-muted-foreground max-w-md mx-auto text-xl">
                  Selecione um dos 25 bichos acima para descobrir suas puxadas, dezenas, centenas e milhares derivados.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </>
  );
};

export default PuxadasPage;
